import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Modal,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useState, useRef, useEffect, useMemo, useLayoutEffect } from "react";
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from "react";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import Select from "react-select";
import { AxiosError } from "axios";

import { STEP_OPTIONS, NEXT_STEP_ACTION } from "./constants";
import {
  OpenRunsRowProps,
  FileDataRow,
  UploadedFile,
  SelectOption,
  ControlQuantificationRow,
  QpcrControlRow,
} from "./types";
import { pickField, convertGDNAFlagToBoolean } from "./utils";
import { StepReagentSelect, StepReagentInstrumentSelect } from "./components";
import SearchDatePicker from "Shared/Common/DatePicker/SearchDatePicker";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import useLang from "Shared/hooks/useLanguage";
import { AddIcon, RemoveICon } from "Shared/Icons";
import {
  StyledDropButtonThreeDots,
  StyledDropMenuMoreAction,
} from "Utils/Style/Dropdownstyle";
import { getTokenData } from "Utils/Auth";
import {
  CreateBatch,
  GetBatchStepDetailById,
  GetBatchStepLogsAsync,
  PostBatchStepDetails,
  UploadQuantificationFile,
} from "Services/Oncology/oncologyService";
import { reactSelectSMStyle, styles } from "Utils/Common";
import { GetBatchReagentLookup } from "Services/BatchReagents/BatchReagents";
import { GetBatchInstrumentLookup } from "Services/Instrument/Instrument";
import RequisitionType from "Services/Requisition/RequisitionTypeService";

// Re-export for backward compatibility
export { getRowId } from "./utils";

const createEmptyQuantificationRows = (count = 7): FileDataRow[] =>
  Array.from({ length: count }, (_, idx) => ({
    id: idx + 1,
    accessionNumber: "",
    nucleicAcidConc: "",
    ratio280: "",
    ratio230: "",
    gDNAFlag: "",
    fail: false,
    dilution: "",
    comments: "",
  }));

const createDefaultQpcrControls = (): QpcrControlRow[] => [];

const PASS_FAIL_OPTIONS = [
  { value: "Pass", label: "Pass" },
  { value: "Fail", label: "Fail" },
];

const normalizePassFailValue = (value: unknown): "Pass" | "Fail" | "" => {
  if (value === true) return "Pass";
  if (value === false) return "Fail";

  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();

  if (["PASS", "PASSED", "TRUE", "1", "Y", "YES", "P"].includes(normalized)) {
    return "Pass";
  }
  if (["FAIL", "FAILED", "FALSE", "0", "N", "NO", "F"].includes(normalized)) {
    return "Fail";
  }
  return "";
};

const getCurrentLoginUserName = (): string => {
  try {
    const tokenData = getTokenData();
    return typeof tokenData?.username === "string"
      ? tokenData.username.trim()
      : "";
  } catch {
    return "";
  }
};

function mapStep3ControlsFromApi(rowsArr: any[]): ControlQuantificationRow[] {
  return rowsArr.map((r: any, idx: number) => {
    const controlId =
      r.controlId ?? r.ControlId ?? r.batchControlId ?? r.BatchControlId;

    return {
      id: idx + 1,
      batchControlId:
        r.batchControlId !== null &&
        r.batchControlId !== undefined &&
        String(r.batchControlId).trim() !== ""
          ? Number(r.batchControlId)
          : r.BatchControlId !== null &&
              r.BatchControlId !== undefined &&
              String(r.BatchControlId).trim() !== ""
            ? Number(r.BatchControlId)
            : null,
      controlId:
        controlId !== null &&
        controlId !== undefined &&
        String(controlId).trim() !== ""
          ? Number(controlId)
          : null,
      controlName: String(r.controlName ?? r.ControlName ?? ""),
      passFail: normalizePassFailValue(
        r.passFail ??
          r.PassFail ??
          r.controlResult ??
          r.ControlResult ??
          r.isPass ??
          r.IsPass
      ),
      nucleicAcidConc:
        r.nucleicAcidConc !== null && r.nucleicAcidConc !== undefined
          ? String(r.nucleicAcidConc)
          : r.NucleicAcidConc !== null && r.NucleicAcidConc !== undefined
            ? String(r.NucleicAcidConc)
            : r.ngPerUl !== null && r.ngPerUl !== undefined
              ? String(r.ngPerUl)
              : r.NgPerUl !== null && r.NgPerUl !== undefined
                ? String(r.NgPerUl)
                : "",
      ratio280:
        r.ratio260_280 !== null && r.ratio260_280 !== undefined
          ? String(r.ratio260_280)
          : r.Ratio260_280 !== null && r.Ratio260_280 !== undefined
            ? String(r.Ratio260_280)
            : r.a260_280 !== null && r.a260_280 !== undefined
              ? String(r.a260_280)
              : r.A260_280 !== null && r.A260_280 !== undefined
                ? String(r.A260_280)
                : "",
      ratio230:
        r.ratio260_230 !== null && r.ratio260_230 !== undefined
          ? String(r.ratio260_230)
          : r.Ratio260_230 !== null && r.Ratio260_230 !== undefined
            ? String(r.Ratio260_230)
            : r.a260_230 !== null && r.a260_230 !== undefined
              ? String(r.a260_230)
              : r.A260_230 !== null && r.A260_230 !== undefined
                ? String(r.A260_230)
                : "",
      gDNAFlag: normalizePassFailValue(
        r.gdnaContaminationFlag ?? r.GdnaContaminationFlag
      ),
      dilution:
        r.dilution !== null && r.dilution !== undefined
          ? String(r.dilution)
          : r.Dilution !== null && r.Dilution !== undefined
            ? String(r.Dilution)
            : "",
      comments: String(r.comments ?? r.Comments ?? ""),
    };
  });
}

interface StepLogEntry {
  stepNumber: number;
  completedBy: string | null;
  completedAt: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  log: string;
}

const toLocalDateTime = (value: string | null) =>
  value ? moment.utc(value).local().format("MMM D, YYYY hh:mm A") : "—";

const buildStepLogText = (entry: {
  stepNumber: number;
  completedBy: string | null;
  completedAt: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}) => {
  const completedBy = entry.completedBy || "Unknown User";
  const completedAt = toLocalDateTime(entry.completedAt);
  const updatedBy = entry.updatedBy?.trim() ? entry.updatedBy : null;
  const updatedAt = toLocalDateTime(entry.updatedAt);

  let text = `${completedBy} completed step ${entry.stepNumber} at ${completedAt}`;
  if (updatedBy) {
    text += `, last updated by ${updatedBy} at ${updatedAt}`;
  }
  return text;
};

function hasRequiredQuantificationValues(
  row: Pick<
    FileDataRow | ControlQuantificationRow,
    "nucleicAcidConc" | "ratio280" | "ratio230"
  >
): boolean {
  return [row.nucleicAcidConc, row.ratio280, row.ratio230].every(
    (value) => String(value ?? "").trim() !== ""
  );
}

function areQuantificationRowsComplete(
  rows: Array<FileDataRow | ControlQuantificationRow>
): boolean {
  return rows.every(hasRequiredQuantificationValues);
}

function isStep6ControlsComplete(rows: QpcrControlRow[]): boolean {
  return (
    rows.length > 0 &&
    rows.every((r) => r.ct.trim() !== "" && r.flag === "Pass")
  );
}

function areStepControlFlagsValid(
  rows: ControlQuantificationRow[],
  isStep5Quant: boolean
): boolean {
  if (rows.length === 0) return true;
  return rows.every((r) =>
    isStep5Quant
      ? r.gDNAFlag === "Pass"
      : r.passFail === "Pass" && r.gDNAFlag === "Pass"
  );
}

function areAccessionFlagsValid(rows: FileDataRow[]): boolean {
  return rows.length > 0 && rows.every((r) => r.gDNAFlag === "Pass");
}

function mapStep6ControlsFromApi(rowsArr: any[]): QpcrControlRow[] {
  return rowsArr.map((r: any, idx: number) => {
    const thresholdRaw = r.controlValue ?? r.ControlValue;
    const thresholdNum =
      thresholdRaw !== null &&
      thresholdRaw !== undefined &&
      String(thresholdRaw).trim() !== ""
        ? Number(String(thresholdRaw).trim())
        : null;
    const threshold = Number.isFinite(thresholdNum as number)
      ? (thresholdNum as number)
      : null;

    const savedCtRaw = r.ctValue ?? r.CTValue ?? r.ct ?? r.Ct;
    const savedCt =
      savedCtRaw !== null &&
      savedCtRaw !== undefined &&
      String(savedCtRaw).trim() !== ""
        ? String(savedCtRaw).trim()
        : "";

    const resultRaw = r.controlResult ?? r.ControlResult;
    const normalizedResult =
      resultRaw === "Pass" || resultRaw === "Fail" ? resultRaw : "";

    const detailId = r.batchControlDetailId ?? r.BatchControlDetailId;
    const ctrlId = r.controlId ?? r.ControlId;

    return {
      id: idx + 1,
      batchControlDetailId:
        detailId !== null &&
        detailId !== undefined &&
        String(detailId).trim() !== ""
          ? Number(detailId)
          : null,
      controlId:
        ctrlId !== null && ctrlId !== undefined && String(ctrlId).trim() !== ""
          ? Number(ctrlId)
          : null,
      controlName: String(r.controlName ?? r.ControlName ?? ""),
      controlValue: threshold,
      ct: savedCt,
      flag: normalizedResult,
      warning: String(r.warning ?? r.Warning ?? "").trim(),
    };
  });
}

/** Read API DTO fields whether JSON is camelCase or PascalCase */
function apiVal(obj: any, camel: string, pascal: string) {
  return obj?.[camel] ?? obj?.[pascal];
}

function toReagentSelectOption(
  value: unknown,
  label: unknown
): SelectOption | null {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;

  return {
    value: numericValue,
    label: label !== null && label !== undefined ? String(label) : "",
  };
}

function readReagentField(reagent: unknown, fields: string[]) {
  if (!reagent || typeof reagent !== "object") return undefined;
  const record = reagent as Record<string, unknown>;
  return fields
    .map((field) => record[field])
    .find((value) => value !== undefined);
}

function mapReagentDtoToSelectOption(reagent: unknown): SelectOption | null {
  const value = readReagentField(reagent, [
    "reagentId",
    "ReagentId",
    "value",
    "Value",
    "id",
    "Id",
  ]);
  const label =
    readReagentField(reagent, [
      "reagentName",
      "ReagentName",
      "label",
      "Label",
      "name",
      "Name",
    ]) ?? "";

  return toReagentSelectOption(value, label);
}

function parseStoredReagentName(value: unknown): unknown[] {
  if (typeof value !== "string") return [];

  const trimmed = value.trim();
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return [];

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
}

function normalizeStepReagents(data: unknown): SelectOption[] {
  const rawReagents = apiVal(data, "reagents", "Reagents");
  const reagentName = apiVal(data, "reagentName", "ReagentName");

  const source = Array.isArray(rawReagents)
    ? rawReagents
    : parseStoredReagentName(reagentName);

  const seen = new Set<string>();
  const selected: SelectOption[] = [];

  source.forEach((item) => {
    const option = mapReagentDtoToSelectOption(item);
    if (!option || seen.has(String(option.value))) return;
    seen.add(String(option.value));
    selected.push(option);
  });

  if (selected.length > 0) return selected;

  const fallback = toReagentSelectOption(
    apiVal(data, "reagentId", "ReagentId"),
    reagentName
  );
  return fallback ? [fallback] : [];
}

/** Renders the step selector control in expanded run detail header. */
const RunDetailStepSelect = ({
  t,
  stepDropdownOptions,
  detailStep,
  maxAllowedDetailStep,
  isStepSaved,
  setPlateNote,
  setDetailStep,
  setIsStepSaved,
}: any) => (
  <div className="d-flex align-items-center gap-1">
    <span className="fw-500" style={{ fontSize: "13px", color: "#333" }}>
      {t("Step #:")}
    </span>
    <Select
      theme={(theme) => styles(theme)}
      options={stepDropdownOptions}
      value={
        stepDropdownOptions.find((o: any) => o.value === detailStep) || null
      }
      styles={{
        ...reactSelectSMStyle,
        control: (provided) => ({
          ...provided,
          minWidth: "220px",
          border: "1px solid #e5e5e5",
          borderRadius: "6px",
          "&:hover": { border: "1px solid #e5e5e5" },
        }),
      }}
      menuPortalTarget={document.body}
      placeholder="--- Select ---"
      isSearchable={false}
      onChange={(opt) => {
        const selected = opt as { value: string; label: string } | null;
        const newStep = parseInt(selected?.value ?? "1", 10);
        if (newStep === 6 && parseInt(detailStep, 10) === 5 && !isStepSaved) {
          toast.warning(
            t("Please save step 5 changes before moving to step 6.")
          );
          return;
        }
        if (newStep === 3 && parseInt(detailStep, 10) === 2 && !isStepSaved) {
          toast.warning(
            t("Please save step 2 changes before moving to step 3.")
          );
          return;
        }
        if (newStep > maxAllowedDetailStep) {
          toast.warning(t("You cannot move ahead of the next allowed step."));
          return;
        }
        setPlateNote("");
        setDetailStep(selected?.value ?? "1");
        setIsStepSaved(true);
      }}
    />
  </div>
);

/** Displays uploaded file metadata rows for quantification steps. */
const UploadedFileTable = ({ t, uploadedFile, onViewFile }: any) => {
  if (!uploadedFile) return null;
  return (
    <TableContainer
      className="shadow-none mt-3"
      style={{ border: "1px solid #D7DAE7", borderRadius: "6px" }}
    >
      <Table aria-label="file upload table" className="mb-0">
        <TableHead>
          <TableRow style={{ backgroundColor: "#F3F6F9" }}>
            <TableCell
              className="min-w-350px fw-bold"
              style={{ borderBottom: "1px solid #D7DAE7" }}
            >
              {t("File")}
            </TableCell>
            <TableCell
              className="min-w-125px w-125px fw-bold"
              style={{ borderBottom: "1px solid #D7DAE7" }}
            >
              {t("Created By")}
            </TableCell>
            <TableCell
              className="min-w-150px w-150px fw-bold"
              style={{ borderBottom: "1px solid #D7DAE7" }}
            >
              {t("Created At")}
            </TableCell>
            <TableCell
              className="min-w-60px w-60px text-center fw-bold"
              style={{ borderBottom: "1px solid #D7DAE7" }}
            >
              {t("View")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell style={{ borderBottom: "none" }}>
              {uploadedFile.fileName}
            </TableCell>
            <TableCell style={{ borderBottom: "none" }}>
              {uploadedFile.createdBy}
            </TableCell>
            <TableCell style={{ borderBottom: "none" }}>
              {uploadedFile.createdAt}
            </TableCell>
            <TableCell className="text-center" style={{ borderBottom: "none" }}>
              <i
                className={`bi bi-eye-fill ${uploadedFile.fileUrl ? "cursor-pointer" : "text-muted"}`}
                style={{
                  color: uploadedFile.fileUrl ? "#FFA800" : "#ADB5BD",
                  fontSize: "18px",
                }}
                onClick={() =>
                  uploadedFile.fileUrl && onViewFile(uploadedFile.fileUrl)
                }
              />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/** Renders step 3/5 quantification grid and per-row inputs. */
const QuantificationDataTable = ({
  t,
  fileDataRows,
  isStep5Quant,
  handleFileDataChange,
  readOnly = false,
}: any) => {
  const allowDecimalKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const allowedControlKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
      "Enter",
    ];

    if (allowedControlKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (e.key >= "0" && e.key <= "9") return;
    if (e.key === "." && !e.currentTarget.value.includes(".")) return;

    e.preventDefault();
  };

  const allowDecimalPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (!/^\d*\.?\d*$/.test(pasted)) {
      e.preventDefault();
    }
  };

  return (
    <TableContainer className="shadow-none mb-3">
      <Table
        stickyHeader
        aria-label="quantification data table"
        className="plate-mapping-table mb-1"
      >
        <TableHead className="h-40px">
          <TableRow>
            <TableCell className="min-w-150px w-150px">
              {t("Accession Number")}
            </TableCell>
            <TableCell className="min-w-125px w-125px">
              {t("Nucleic Acid Conc.")} *
            </TableCell>
            <TableCell className="min-w-100px w-100px">
              {t("260/280")} *
            </TableCell>
            <TableCell className="min-w-100px w-100px">
              {t("260/230")} *
            </TableCell>
            <TableCell className="min-w-150px w-150px">
              {t("gDNA contamination Flag")}
            </TableCell>
            {/* <TableCell className="min-w-60px w-60px">
              <label className="form-check form-check-inline form-check-solid m-0 fw-500">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={
                    fileDataRows.length > 0 &&
                    fileDataRows.every((r: any) => r.fail)
                  }
                  onChange={(e) => {
                    setFileDataRows((prev: any) =>
                      prev.map((r: any) => ({ ...r, fail: e.target.checked }))
                    );
                    markStepDirty();
                  }}
                />
                {t("Fail")}
              </label>
            </TableCell> */}
            {isStep5Quant && (
              <TableCell className="min-w-110px w-110px">
                {t("Dilution")}
              </TableCell>
            )}
            <TableCell className="min-w-200px">{t("Comments")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody className="scroll-start">
          {fileDataRows.map((dataRow: any) => (
            <TableRow key={dataRow.id}>
              <TableCell>
                <div className="py-2 px-1" style={{ minHeight: "38px" }}>
                  {dataRow.accessionNumber || "—"}
                </div>
              </TableCell>
              <TableCell>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control bg-transparent"
                  value={dataRow.nucleicAcidConc}
                  required
                  readOnly={readOnly}
                  onKeyDown={allowDecimalKeyDown}
                  onPaste={allowDecimalPaste}
                  onChange={
                    readOnly
                      ? undefined
                      : (e) =>
                          handleFileDataChange(
                            dataRow.id,
                            "nucleicAcidConc",
                            e.target.value
                          )
                  }
                />
              </TableCell>
              <TableCell>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control bg-transparent"
                  value={dataRow.ratio280}
                  required
                  readOnly={readOnly}
                  onKeyDown={allowDecimalKeyDown}
                  onPaste={allowDecimalPaste}
                  onChange={
                    readOnly
                      ? undefined
                      : (e) =>
                          handleFileDataChange(
                            dataRow.id,
                            "ratio280",
                            e.target.value
                          )
                  }
                />
              </TableCell>
              <TableCell>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control bg-transparent"
                  value={dataRow.ratio230}
                  required
                  readOnly={readOnly}
                  onKeyDown={allowDecimalKeyDown}
                  onPaste={allowDecimalPaste}
                  onChange={
                    readOnly
                      ? undefined
                      : (e) =>
                          handleFileDataChange(
                            dataRow.id,
                            "ratio230",
                            e.target.value
                          )
                  }
                />
              </TableCell>
              <TableCell>
                <select
                  className="form-select bg-transparent"
                  value={normalizePassFailValue(dataRow.gDNAFlag)}
                  disabled={readOnly}
                  onChange={
                    readOnly
                      ? undefined
                      : (e) =>
                          handleFileDataChange(
                            dataRow.id,
                            "gDNAFlag",
                            e.target.value
                          )
                  }
                >
                  <option value="">--- Select ---</option>
                  {PASS_FAIL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.label)}
                    </option>
                  ))}
                </select>
              </TableCell>
              {/* <TableCell>
                <label className="form-check form-check-inline form-check-solid m-0 fw-500">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={dataRow.fail}
                    onChange={(e) =>
                      handleFileDataChange(dataRow.id, "fail", e.target.checked)
                    }
                  />
                </label>
              </TableCell> */}
              {isStep5Quant && (
                <TableCell>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    className="form-control bg-transparent"
                    value={dataRow.dilution}
                    readOnly={readOnly}
                    onKeyDown={allowDecimalKeyDown}
                    onPaste={allowDecimalPaste}
                    onChange={
                      readOnly
                        ? undefined
                        : (e) =>
                            handleFileDataChange(
                              dataRow.id,
                              "dilution",
                              e.target.value
                            )
                    }
                  />
                </TableCell>
              )}
              <TableCell>
                <input
                  type="text"
                  autoComplete="off"
                  className="form-control bg-transparent"
                  value={dataRow.comments}
                  readOnly={readOnly}
                  onChange={
                    readOnly
                      ? undefined
                      : (e) =>
                          handleFileDataChange(
                            dataRow.id,
                            "comments",
                            e.target.value
                          )
                  }
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/** Renders step 3 / 5 control quantification grid and per-row inputs. */
const ControlQuantificationDataTable = ({
  t,
  controlRows,
  handleControlRowChange,
  isStep5Quant = false,
  readOnly = false,
}: any) => {
  const allowDecimalKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const allowedControlKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
      "Enter",
    ];

    if (allowedControlKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (e.key >= "0" && e.key <= "9") return;
    if (e.key === "." && !e.currentTarget.value.includes(".")) return;

    e.preventDefault();
  };

  const allowDecimalPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (!/^\d*\.?\d*$/.test(pasted)) {
      e.preventDefault();
    }
  };

  if (!controlRows.length) return null;

  return (
    <div className="mt-4">
      <div className="fw-bold mb-2" style={{ color: "#3F4254" }}>
        {t("Controls")}
      </div>
      <TableContainer className="shadow-none mb-3">
        <Table
          stickyHeader
          aria-label="control quantification data table"
          className="plate-mapping-table mb-1"
        >
          <TableHead className="h-40px">
            <TableRow>
              <TableCell className="min-w-150px w-150px">
                {t("Control")}
              </TableCell>
              {!isStep5Quant && (
                <TableCell className="min-w-110px w-110px">
                  {t("Pass/Fail")}
                </TableCell>
              )}
              <TableCell className="min-w-125px w-125px">
                {t("Nucleic Acid Conc.")} *
              </TableCell>
              <TableCell className="min-w-100px w-100px">
                {t("260/280")} *
              </TableCell>
              <TableCell className="min-w-100px w-100px">
                {t("260/230")} *
              </TableCell>
              <TableCell className="min-w-150px w-150px">
                {t("gDNA contamination Flag")}
              </TableCell>
              {isStep5Quant && (
                <TableCell className="min-w-110px w-110px">
                  {t("Dilution")}
                </TableCell>
              )}
              <TableCell className="min-w-200px">{t("Comments")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="scroll-start">
            {controlRows.map((controlRow: ControlQuantificationRow) => (
              <TableRow key={controlRow.id}>
                <TableCell>
                  <div className="py-2 px-1" style={{ minHeight: "38px" }}>
                    {controlRow.controlName || "---"}
                  </div>
                </TableCell>
                {!isStep5Quant && (
                  <TableCell>
                    <select
                      className="form-select bg-transparent"
                      value={controlRow.passFail}
                      disabled={readOnly}
                      onChange={
                        readOnly
                          ? undefined
                          : (e) =>
                              handleControlRowChange(
                                controlRow.id,
                                "passFail",
                                e.target.value
                              )
                      }
                    >
                      <option value="">--- Select ---</option>
                      {PASS_FAIL_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {t(option.label)}
                        </option>
                      ))}
                    </select>
                  </TableCell>
                )}
                <TableCell>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    className="form-control bg-transparent"
                    value={controlRow.nucleicAcidConc}
                    required
                    readOnly={readOnly}
                    onKeyDown={allowDecimalKeyDown}
                    onPaste={allowDecimalPaste}
                    onChange={
                      readOnly
                        ? undefined
                        : (e) =>
                            handleControlRowChange(
                              controlRow.id,
                              "nucleicAcidConc",
                              e.target.value
                            )
                    }
                  />
                </TableCell>
                <TableCell>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    className="form-control bg-transparent"
                    value={controlRow.ratio280}
                    required
                    readOnly={readOnly}
                    onKeyDown={allowDecimalKeyDown}
                    onPaste={allowDecimalPaste}
                    onChange={
                      readOnly
                        ? undefined
                        : (e) =>
                            handleControlRowChange(
                              controlRow.id,
                              "ratio280",
                              e.target.value
                            )
                    }
                  />
                </TableCell>
                <TableCell>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    className="form-control bg-transparent"
                    value={controlRow.ratio230}
                    required
                    readOnly={readOnly}
                    onKeyDown={allowDecimalKeyDown}
                    onPaste={allowDecimalPaste}
                    onChange={
                      readOnly
                        ? undefined
                        : (e) =>
                            handleControlRowChange(
                              controlRow.id,
                              "ratio230",
                              e.target.value
                            )
                    }
                  />
                </TableCell>
                <TableCell>
                  <select
                    className="form-select bg-transparent"
                    value={controlRow.gDNAFlag}
                    disabled={readOnly}
                    onChange={
                      readOnly
                        ? undefined
                        : (e) =>
                            handleControlRowChange(
                              controlRow.id,
                              "gDNAFlag",
                              e.target.value
                            )
                    }
                  >
                    <option value="">--- Select ---</option>
                    {PASS_FAIL_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.label)}
                      </option>
                    ))}
                  </select>
                </TableCell>
                {isStep5Quant && (
                  <TableCell>
                    <input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      className="form-control bg-transparent"
                      value={controlRow.dilution}
                      readOnly={readOnly}
                      onKeyDown={allowDecimalKeyDown}
                      onPaste={allowDecimalPaste}
                      onChange={
                        readOnly
                          ? undefined
                          : (e) =>
                              handleControlRowChange(
                                controlRow.id,
                                "dilution",
                                e.target.value
                              )
                      }
                    />
                  </TableCell>
                )}
                <TableCell>
                  <input
                    type="text"
                    autoComplete="off"
                    className="form-control bg-transparent"
                    value={controlRow.comments}
                    readOnly={readOnly}
                    onChange={
                      readOnly
                        ? undefined
                        : (e) =>
                            handleControlRowChange(
                              controlRow.id,
                              "comments",
                              e.target.value
                            )
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

function OpenRunsRow({
  row,
  index,
  isOpen,
  onToggleExpand,
  isSelected,
  onToggleSelect,
  setLocalRows = () => undefined,
  setIsBtnDisabled = () => undefined,
  refetch = () => undefined,
  viewOnly = false,
}: OpenRunsRowProps) {
  const { t } = useLang();
  const currentLoginUserName = useMemo(() => getCurrentLoginUserName(), []);

  // UI State
  const [anchorEl, setAnchorEl] = useState<any>({ dropdown1: null });

  // Form State
  const [plateNote, setPlateNote] = useState("");
  const [selectedReagents, setSelectedReagents] = useState<SelectOption[]>([]);
  const [selectedInstrument, setSelectedInstrument] = useState("");
  const [detailStep, setDetailStep] = useState(
    String(pickField(row, "stepNumber") || "1")
  );

  // Step Progress State
  const [isStepSaved, setIsStepSaved] = useState(true);
  const [completedStep, setCompletedStep] = useState(
    parseInt(pickField(row, "completedStep") || "0")
  );

  // File Upload State
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileDataRows, setFileDataRows] = useState<FileDataRow[]>(() =>
    createEmptyQuantificationRows()
  );
  const [controlQuantificationRows, setControlQuantificationRows] = useState<
    ControlQuantificationRow[]
  >([]);
  const [qpcrControlRows, setQpcrControlRows] = useState<QpcrControlRow[]>(() =>
    createDefaultQpcrControls()
  );
  const [stepDetailLoading, setStepDetailLoading] = useState(false);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [isLogLoading, setIsLogLoading] = useState(false);
  const [stepLogs, setStepLogs] = useState<StepLogEntry[]>([]);

  useEffect(() => {
    const stepNum = parseInt(detailStep, 10);
    if (stepNum !== 2 && stepNum !== 4) setSelectedReagents([]);
    if (stepNum !== 4) setSelectedInstrument("");
    setControlQuantificationRows([]);
  }, [detailStep]);

  // Computed Values
  const isNewRow = row.oncologyBatchId === 0;
  const batchName = pickField(row, "batchName", "name");
  const batchCode = pickField(row, "batchCode", "batchNumber");
  const runRaw = pickField(row, "runDate", "runDateTime", "createdDate");
  const runDisplay = runRaw ? moment(runRaw).format("M/D/YYYY") : "";
  const plateNo = pickField(
    row,
    "plateBarcodeNo",
    "plateBarcode",
    "plateBarcodeNumber"
  );
  const stepLabel =
    pickField(row, "stepDescription", "stepName", "step") ||
    pickField(row, "stepNumber", "stepCode") ||
    "";

  const batchId = row.batchId ?? row.oncologyBatchId ?? 0;
  const batchCurrentStep = useMemo(() => {
    const n = parseInt(String(pickField(row, "stepNumber") || "1"), 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }, [row]);
  const [effectiveBatchStep, setEffectiveBatchStep] =
    useState(batchCurrentStep);

  useEffect(() => {
    setEffectiveBatchStep(batchCurrentStep);
  }, [batchCurrentStep]);

  const batchCurrentStepRef = useRef(effectiveBatchStep);
  useLayoutEffect(() => {
    batchCurrentStepRef.current = effectiveBatchStep;
  }, [effectiveBatchStep]);

  const maxAllowedDetailStep = useMemo(() => {
    // In expanded step selector, do not expose steps ahead of batch current step.
    return Math.min(6, Math.max(1, effectiveBatchStep));
  }, [effectiveBatchStep]);

  const stepDropdownOptions = useMemo(
    () =>
      STEP_OPTIONS.filter(
        (o) => o.value !== "" && parseInt(o.value, 10) <= maxAllowedDetailStep
      ),
    [maxAllowedDetailStep]
  );
  const displayedStepLabel =
    STEP_OPTIONS.find((o) => o.value === String(effectiveBatchStep))?.label ||
    stepLabel;

  // Fetch step detail when viewing a step at or behind the batch's current step.
  // Steps 1–6 fetch on the current step; any step behind the current step also fetches (review).
  useEffect(() => {
    if (!isOpen || isNewRow || !batchId) return;
    const stepNum = parseInt(detailStep, 10);
    if (!Number.isFinite(stepNum) || stepNum < 1 || stepNum > 6) return;

    const batchStep = batchCurrentStepRef.current;
    if (stepNum > batchStep) return;

    const fetchOnCurrentStep = stepNum >= 1 && stepNum <= 6;
    if (!fetchOnCurrentStep && stepNum >= batchStep) return;

    let cancelled = false;
    setStepDetailLoading(true);

    (async () => {
      try {
        const res = await GetBatchStepDetailById(batchId, stepNum);
        if (cancelled) return;

        const envelope = res?.data;
        const status = envelope?.statusCode ?? envelope?.StatusCode;
        // Steps 3, 5, 6 always have pre-saved accessions/controls from batch
        // creation, so a 404 there is a real error. Steps 1, 2, 4 have no data
        // until the user submits them, so a 404 on the current active step is
        // expected and should be silenced.
        const isCurrentActiveStep = stepNum === batchCurrentStepRef.current;
        const stepAlwaysHasData = [3, 5, 6].includes(stepNum);
        const isExpectedEmpty = isCurrentActiveStep && !stepAlwaysHasData;
        if (
          status !== null &&
          status !== undefined &&
          status !== 200 &&
          status !== 201
        ) {
          if (status === 404 && isExpectedEmpty) return;
          toast.error(
            envelope?.message ??
              envelope?.Message ??
              t("Failed to load step data.")
          );
          return;
        }

        const data = envelope?.data ?? envelope?.Data ?? envelope;
        if (
          data === null ||
          data === undefined ||
          (typeof data !== "object" && !Array.isArray(data))
        ) {
          if (isExpectedEmpty) return;
          toast.error(t("Failed to load step data."));
          return;
        }

        if (stepNum === 1) {
          setPlateNote(String(apiVal(data, "notes", "Notes") ?? ""));
        } else if (stepNum === 2) {
          setPlateNote(String(apiVal(data, "notes", "Notes") ?? ""));
          const reagents = normalizeStepReagents(data);
          setSelectedReagents(reagents);
          setIsStepSaved(reagents.length > 0);
        } else if (stepNum === 3 || stepNum === 5) {
          setPlateNote(String(apiVal(data, "notes", "Notes") ?? ""));
          const fn = apiVal(data, "fileName", "FileName");
          const fu = apiVal(data, "fileUrl", "FileUrl");
          const cb = apiVal(data, "createdBy", "CreatedBy");
          const ca = apiVal(data, "createdAt", "CreatedAt");
          if (fn && fu) {
            setUploadedFile({
              fileName: String(fn),
              fileUrl: String(fu),
              createdBy: cb !== null && cb !== undefined ? String(cb) : "",
              createdAt:
                ca !== null && ca !== undefined && String(ca).trim() !== ""
                  ? moment(String(ca)).format("YYYY-MM-DD hh:mm A")
                  : "",
            });
          } else {
            setUploadedFile(null);
          }
          const rowsArr = (apiVal(data, "rows", "Rows") as any[]) ?? [];
          const mapped: FileDataRow[] = rowsArr.map((r, idx) => ({
            id: idx + 1,
            accessionNumber: String(
              r.accessionNumber ?? r.AccessionNumber ?? ""
            ),
            nucleicAcidConc:
              r.ngPerUl !== null && r.ngPerUl !== undefined
                ? String(r.ngPerUl)
                : r.NgPerUl !== null && r.NgPerUl !== undefined
                  ? String(r.NgPerUl)
                  : "",
            ratio280:
              r.a260_280 !== null && r.a260_280 !== undefined
                ? String(r.a260_280)
                : r.A260_280 !== null && r.A260_280 !== undefined
                  ? String(r.A260_280)
                  : "",
            ratio230:
              r.a260_230 !== null && r.a260_230 !== undefined
                ? String(r.a260_230)
                : r.A260_230 !== null && r.A260_230 !== undefined
                  ? String(r.A260_230)
                  : "",
            gDNAFlag: normalizePassFailValue(
              r.gdnaContaminationFlag ?? r.GdnaContaminationFlag
            ),
            fail: Boolean(r.isFail ?? r.IsFail),
            dilution:
              r.dilution !== null && r.dilution !== undefined
                ? String(r.dilution)
                : r.Dilution !== null && r.Dilution !== undefined
                  ? String(r.Dilution)
                  : "",
            comments: String(r.comments ?? r.Comments ?? ""),
          }));
          setFileDataRows(
            mapped.length > 0 ? mapped : createEmptyQuantificationRows()
          );
          const controlRowsArr =
            (apiVal(
              data,
              "controlQuantificationData",
              "ControlQuantificationData"
            ) as any[]) ??
            (apiVal(data, "controlRows", "ControlRows") as any[]) ??
            (apiVal(data, "controls", "Controls") as any[]) ??
            [];
          if (controlRowsArr.length > 0) {
            setControlQuantificationRows(
              mapStep3ControlsFromApi(controlRowsArr)
            );
          }
        } else if (stepNum === 4) {
          setPlateNote(String(apiVal(data, "notes", "Notes") ?? ""));
          const iid = apiVal(data, "instrumentId", "InstrumentId");
          setSelectedReagents(normalizeStepReagents(data));
          setSelectedInstrument(
            iid !== null && iid !== undefined && iid !== "" ? String(iid) : ""
          );
        } else if (stepNum === 6) {
          let mappedControls: QpcrControlRow[] = [];
          if (Array.isArray(data)) {
            setPlateNote("");
            setUploadedFile(null);
            mappedControls = mapStep6ControlsFromApi(data);
          } else {
            setPlateNote(String(apiVal(data, "notes", "Notes") ?? ""));
            const fn = apiVal(data, "fileName", "FileName");
            const fu = apiVal(data, "fileUrl", "FileUrl");
            const cb = apiVal(data, "createdBy", "CreatedBy");
            const ca = apiVal(data, "createdAt", "CreatedAt");
            if (fn && fu) {
              setUploadedFile({
                fileName: String(fn),
                fileUrl: String(fu),
                createdBy: cb !== null && cb !== undefined ? String(cb) : "",
                createdAt:
                  ca !== null && ca !== undefined && String(ca).trim() !== ""
                    ? moment(String(ca)).format("YYYY-MM-DD hh:mm A")
                    : "",
              });
            } else {
              setUploadedFile(null);
            }
            const rowsArr =
              (apiVal(data, "controls", "Controls") as any[]) ??
              (apiVal(data, "step6Controls", "Step6Controls") as any[]) ??
              (apiVal(data, "rows", "Rows") as any[]) ??
              [];
            mappedControls =
              rowsArr.length > 0
                ? mapStep6ControlsFromApi(rowsArr)
                : createDefaultQpcrControls();
          }
          setQpcrControlRows(mappedControls);
          setIsStepSaved(isStep6ControlsComplete(mappedControls));
          return;
        }

        setIsStepSaved(true);
      } catch (e) {
        if (!cancelled) {
          const status =
            (e as any)?.response?.status ??
            (e as any)?.response?.data?.statusCode;
          const isExpectedEmptyOnCatch =
            stepNum === batchCurrentStepRef.current &&
            ![3, 5, 6].includes(stepNum);
          if (status === 404 && isExpectedEmptyOnCatch) return;
          console.error(e);
          toast.error(t("Failed to load step data."));
        }
      } finally {
        if (!cancelled) setStepDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isNewRow, batchId, detailStep, t]);

  // Lookup Queries
  const { data: reagentLookupData } = useQuery(
    "batch-reagent-lookup",
    GetBatchReagentLookup,
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      select: (res: any) => {
        const data = res?.data?.data || res?.data || [];
        return Array.isArray(data) ? data : [];
      },
    }
  );
  const reagentOptions: SelectOption[] = reagentLookupData ?? [];

  const { data: instrumentLookupData } = useQuery(
    "batch-instrument-lookup",
    GetBatchInstrumentLookup,
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      select: (res: any) => {
        const data = res?.data?.data || res?.data || [];
        return Array.isArray(data) ? data : [];
      },
    }
  );
  const instrumentOptions: SelectOption[] = instrumentLookupData ?? [];

  // Dropdown Handlers
  const handleClick = (event: any, name: string) =>
    setAnchorEl({ ...anchorEl, [name]: event.currentTarget });
  const handleClose = (name: string) =>
    setAnchorEl({ ...anchorEl, [name]: null });

  // Row Data Handlers
  const handleChange = (field: string, value: string) => {
    if (viewOnly) return;

    if (isNewRow) {
      setLocalRows((curr) =>
        curr.map((x, i) => (i === index ? { ...x, [field]: value } : x))
      );
    } else {
      setLocalRows((curr) =>
        curr.map((x) =>
          x.oncologyBatchId === row.oncologyBatchId
            ? { ...x, [field]: value }
            : x
        )
      );
    }
  };

  const removeFromLocalRows = () => {
    if (isNewRow) {
      setLocalRows((curr) => curr.filter((_, i) => i !== index));
    } else {
      setLocalRows((curr) =>
        curr.filter((x) => x.oncologyBatchId !== row.oncologyBatchId)
      );
    }
  };

  const handleRowCancel = () => {
    if (viewOnly) return;

    handleClose("dropdown1");
    removeFromLocalRows();
    if (isNewRow) setIsBtnDisabled(false);
  };

  // File Data Handlers
  const handleFileDataChange = (id: number, field: string, value: any) => {
    const decimalOnlyFields = [
      "nucleicAcidConc",
      "ratio280",
      "ratio230",
      "dilution",
    ];
    const sanitizeDecimalInput = (input: string) => {
      const normalized = input.replace(/,/g, ".");
      const cleaned = normalized.replace(/[^0-9.]/g, "");
      const [whole = "", ...fractionParts] = cleaned.split(".");
      if (fractionParts.length === 0) return whole;
      return `${whole}.${fractionParts.join("")}`;
    };

    const nextValue =
      decimalOnlyFields.includes(field) && typeof value === "string"
        ? sanitizeDecimalInput(value)
        : value;

    setFileDataRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: nextValue } : r))
    );
    markStepDirty();
  };

  const handleControlQuantificationChange = (
    id: number,
    field: string,
    value: any
  ) => {
    const decimalOnlyFields = [
      "nucleicAcidConc",
      "ratio280",
      "ratio230",
      "dilution",
    ];
    const sanitizeDecimalInput = (input: string) => {
      const normalized = input.replace(/,/g, ".");
      const cleaned = normalized.replace(/[^0-9.]/g, "");
      const [whole = "", ...fractionParts] = cleaned.split(".");
      if (fractionParts.length === 0) return whole;
      return `${whole}.${fractionParts.join("")}`;
    };

    const nextValue =
      decimalOnlyFields.includes(field) && typeof value === "string"
        ? sanitizeDecimalInput(value)
        : value;

    setControlQuantificationRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: nextValue } : r))
    );
    markStepDirty();
  };

  const handleFileUploadClick = () => {
    if (viewOnly) return;
    fileInputRef.current?.click();
  };

  const handleViewUploadedFile = (url: string) => {
    if (!url) return;
    RequisitionType.ShowBlob(url)
      .then((res: any) => {
        const blobUrl = res?.data?.Data ?? res?.data?.data ?? "";
        if (!blobUrl) {
          toast.error(t("Unable to open file."));
          return;
        }
        window.open(String(blobUrl).replace("}", ""), "_blank");
      })
      .catch((err: any) => {
        console.error(err);
        toast.error(t("Unable to open file."));
      });
  };

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    if (viewOnly) return;

    const file = event.target.files?.[0];
    if (!file) return;

    if (!batchId) {
      toast.error(t("Batch ID is required for file upload."));
      return;
    }

    const stepNum = parseInt(detailStep, 10);
    if (stepNum !== 3 && stepNum !== 5 && stepNum !== 6) {
      toast.error(
        t("File upload is only allowed for step 3, step 5, or step 6.")
      );
      return;
    }

    setIsFileUploading(true);
    try {
      const response = await UploadQuantificationFile(batchId, stepNum, file);
      const envelope = response?.data;
      const statusCode = envelope?.statusCode ?? envelope?.StatusCode;
      const message = envelope?.message ?? envelope?.Message;
      const data = envelope?.data ?? envelope?.Data ?? null;

      if (
        statusCode !== null &&
        statusCode !== undefined &&
        statusCode !== 200 &&
        statusCode !== 201
      ) {
        toast.error(message || t("Failed to upload file."));
        return;
      }

      if (data) {
        const uploadCreatedBy = data.createdBy ?? data.CreatedBy;
        setUploadedFile({
          fileName: data.fileName || data.FileName || file.name,
          fileUrl: data.fileUrl || data.FileUrl || "",
          createdBy:
            uploadCreatedBy !== null &&
            uploadCreatedBy !== undefined &&
            String(uploadCreatedBy).trim() !== ""
              ? String(uploadCreatedBy)
              : currentLoginUserName,
          createdAt: moment().format("YYYY-MM-DD hh:mm A"),
        });

        if (stepNum === 6) {
          const step6ControlsArr =
            (data.step6Controls as any[]) ??
            (data.Step6Controls as any[]) ??
            (data.controls as any[]) ??
            (data.Controls as any[]) ??
            [];

          if (step6ControlsArr.length > 0) {
            const mappedControls = mapStep6ControlsFromApi(step6ControlsArr);
            setQpcrControlRows(mappedControls);
          }

          toast.success(t("File uploaded and parsed successfully."));
          markStepDirty();
          return;
        }

        const controlRowsArr =
          (data.controlRows as any[]) ?? (data.ControlRows as any[]) ?? [];

        if (controlRowsArr.length > 0) {
          setControlQuantificationRows(mapStep3ControlsFromApi(controlRowsArr));
        }

        const rows = ((data.rows as any[]) ?? (data.Rows as any[]) ?? []).map(
          (r: any, idx: number) => ({
            id: idx + 1,
            accessionNumber: r.accessionNumber || "",
            nucleicAcidConc: r.ngPerUl?.toString() || "",
            ratio280: r.a260_280?.toString() || "",
            ratio230: r.a260_230?.toString() || "",
            gDNAFlag: normalizePassFailValue(
              r.gdnaContaminationFlag ?? r.gDNAContaminationFlag
            ),
            fail: false,
            dilution:
              r.dilution !== null && r.dilution !== undefined
                ? String(r.dilution)
                : r.Dilution !== null && r.Dilution !== undefined
                  ? String(r.Dilution)
                  : "",
            comments: "",
          })
        );

        setFileDataRows(rows);
        toast.success(t("File uploaded and parsed successfully."));
        markStepDirty();
      } else {
        toast.error(message || t("Failed to parse file data."));
      }
    } catch (error) {
      console.error("File upload error:", error);
      toast.error(t("Failed to upload file."));
    } finally {
      setIsFileUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Save Mutations
  const saveMutation = useMutation((payload: any) => CreateBatch(payload), {
    onSuccess: (res: any) => {
      if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
        toast.success(
          res?.data?.data?.message ?? t("Batch created successfully.")
        );
        setIsBtnDisabled(false);
        handleClose("dropdown1");
        removeFromLocalRows();
        refetch();
      } else {
        toast.error(res?.data?.data?.message ?? t("Something went wrong."));
      }
    },
    onError: () => {
      toast.error(t("An unexpected error occurred."));
    },
  });

  const handleSave = () => {
    if (viewOnly) return;

    if (!row.batchName?.trim()) {
      toast.error(t("Batch name cannot be empty."));
      return;
    }
    saveMutation.mutate({
      oncologyBatchId: row.oncologyBatchId ?? 0,
      batchName: row.batchName,
      batchCode: row.batchCode ?? "",
      runDate: row.runDate ?? "",
      plateBarcodeNo: row.plateBarcodeNo ?? "",
      stepNumber: row.stepNumber ?? "",
    });
  };

  // Step Save Logic
  const stepSaveMutation = useMutation(
    (payload: any) => PostBatchStepDetails(payload),
    {
      onSuccess: (res: any) => {
        if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
          toast.success(
            res?.data?.message ?? t("Step data saved successfully.")
          );
          setIsStepSaved(true);
          const currentStepNum = parseInt(detailStep);
          if (currentStepNum > completedStep) setCompletedStep(currentStepNum);
          setEffectiveBatchStep((prev) => Math.max(prev, currentStepNum));
          if (currentStepNum === 6) {
            refetch();
          }
        } else {
          toast.error(res?.data?.message ?? t("Failed to save step data."));
        }
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<any>;
        const serverMessage =
          axiosError?.response?.data?.message ??
          axiosError?.response?.data?.Message;
        toast.error(
          serverMessage ||
            t("An unexpected error occurred while saving step data.")
        );
      },
    }
  );

  const getSelectedReagentPayload = () => {
    const reagents: { reagentId: number; reagentName: string | null }[] = [];

    selectedReagents.forEach((selected) => {
      const option =
        reagentOptions.find(
          (reagent) => String(reagent.value) === String(selected.value)
        ) || selected;
      const reagentId = Number(option.value);

      if (!Number.isFinite(reagentId)) return;

      reagents.push({
        reagentId,
        reagentName: option.label || null,
      });
    });

    return reagents;
  };

  const applySelectedReagentsToPayload = (payload: any) => {
    const reagents = getSelectedReagentPayload();
    const primaryReagent = reagents[0];

    payload.reagentId = primaryReagent?.reagentId ?? null;
    payload.reagentName = primaryReagent?.reagentName ?? null;
    payload.reagents = reagents;
  };

  const buildStepPayload = () => {
    const stepNum = parseInt(detailStep);
    const payload: any = {
      oncologyBatchId: batchId,
      stepNumber: stepNum,
      note: plateNote,
    };

    if (stepNum === 2) {
      applySelectedReagentsToPayload(payload);
    }

    if (stepNum === 3 || stepNum === 5) {
      payload.fileId = null;
      payload.fileName = uploadedFile?.fileName || null;
      payload.fileUrl = uploadedFile?.fileUrl || null;
      payload.quantificationData = fileDataRows.map((r, idx) => {
        const row: Record<string, unknown> = {
          rowOrder: idx + 1,
          accessionNumber: r.accessionNumber,
          nucleicAcidConc: r.nucleicAcidConc
            ? parseFloat(r.nucleicAcidConc)
            : null,
          ratio260_280: r.ratio280 ? parseFloat(r.ratio280) : null,
          ratio260_230: r.ratio230 ? parseFloat(r.ratio230) : null,
          gdnaContaminationFlag: convertGDNAFlagToBoolean(r.gDNAFlag),
          isFail: r.fail,
          comments: r.comments || null,
        };
        if (stepNum === 5) {
          row.dilution = r.dilution ? parseFloat(r.dilution) : null;
        }
        return row;
      });
      payload.controlQuantificationData = controlQuantificationRows.map(
        (r, idx) => ({
          rowOrder: idx + 1,
          batchControlId: r.batchControlId ?? null,
          controlId: r.controlId ?? r.batchControlId ?? null,
          controlName: r.controlName,
          controlResult: stepNum === 5 ? null : r.passFail || null,
          isPass:
            stepNum === 5
              ? null
              : r.passFail === "Pass"
                ? true
                : r.passFail === "Fail"
                  ? false
                  : null,
          nucleicAcidConc: r.nucleicAcidConc
            ? parseFloat(r.nucleicAcidConc)
            : null,
          ratio260_280: r.ratio280 ? parseFloat(r.ratio280) : null,
          ratio260_230: r.ratio230 ? parseFloat(r.ratio230) : null,
          gdnaContaminationFlag: convertGDNAFlagToBoolean(r.gDNAFlag),
          dilution: stepNum === 5 && r.dilution ? parseFloat(r.dilution) : null,
          comments: r.comments || null,
        })
      );
    }

    if (stepNum === 4) {
      applySelectedReagentsToPayload(payload);
      payload.instrumentId = selectedInstrument
        ? parseInt(selectedInstrument)
        : null;
      payload.instrumentName =
        instrumentOptions.find((i) => String(i.value) === selectedInstrument)
          ?.label || null;
    }

    if (stepNum === 6) {
      payload.fileId = null;
      payload.fileName = uploadedFile?.fileName || null;
      payload.fileUrl = uploadedFile?.fileUrl || null;
      payload.step6Controls = qpcrControlRows.map((r) => ({
        batchControlDetailId: r.batchControlDetailId ?? null,
        batchId: batchId || null,
        stepNumber: stepNum || null,
        controlId: r.controlId ?? null,
        controlName: r.controlName,
        controlValue:
          r.controlValue !== null && r.controlValue !== undefined
            ? String(r.controlValue)
            : "",
        ctValue: r.ct || null,
        controlResult: r.flag || null,
        warning: r.warning || null,
      }));
    }

    return payload;
  };

  const canSaveCurrentStep = () => {
    const stepNum = parseInt(detailStep);
    if (stepNum === 2) return selectedReagents.length > 0;
    if (stepNum === 3 || stepNum === 5) {
      return (
        fileDataRows.length > 0 &&
        areQuantificationRowsComplete(fileDataRows) &&
        areQuantificationRowsComplete(controlQuantificationRows) &&
        areAccessionFlagsValid(fileDataRows) &&
        areStepControlFlagsValid(controlQuantificationRows, stepNum === 5)
      );
    }
    if (stepNum === 4)
      return selectedReagents.length > 0 && selectedInstrument !== "";
    if (stepNum === 6) return isStep6ControlsComplete(qpcrControlRows);
    return stepNum === 1;
  };

  const canBeginNextStep = () => {
    const stepNum = parseInt(detailStep, 10);
    if (stepNum === 2) {
      return selectedReagents.length > 0 && isStepSaved;
    }
    return isStepSaved && canSaveCurrentStep();
  };

  const handleSaveStepChanges = () => {
    if (viewOnly) return;

    const stepNum = parseInt(detailStep);

    if (stepNum === 1) {
      stepSaveMutation.mutate(buildStepPayload());
    } else if (stepNum === 2) {
      if (selectedReagents.length === 0) {
        toast.warning(t("Please select at least one reagent."));
        return;
      }
      stepSaveMutation.mutate(buildStepPayload());
    } else if (stepNum === 3 || stepNum === 5) {
      if (fileDataRows.length === 0) {
        toast.warning(t("Please add at least one row."));
        return;
      }
      if (
        !areQuantificationRowsComplete(fileDataRows) ||
        !areQuantificationRowsComplete(controlQuantificationRows)
      ) {
        toast.warning(
          t(
            "Nucleic Acid Conc., 260/280, and 260/230 are required for all accession and control rows."
          )
        );
        return;
      }
      if (!areAccessionFlagsValid(fileDataRows)) {
        toast.warning(
          t(
            "All accession gDNA contamination Flag values must be Pass before saving."
          )
        );
        return;
      }
      if (!areStepControlFlagsValid(controlQuantificationRows, stepNum === 5)) {
        toast.warning(
          stepNum === 5
            ? t(
                "All control gDNA contamination Flag values must be Pass before saving."
              )
            : t(
                "All control Pass/Fail and gDNA contamination Flag values must be Pass before saving."
              )
        );
        return;
      }
      stepSaveMutation.mutate(buildStepPayload());
    } else if (stepNum === 4) {
      if (selectedReagents.length === 0 || !selectedInstrument) {
        toast.warning(t("Please select reagents and instrument."));
        return;
      }
      stepSaveMutation.mutate(buildStepPayload());
    } else if (stepNum === 6) {
      if (!isStep6ControlsComplete(qpcrControlRows)) {
        toast.warning(
          t(
            "CT is required and Pass/Fail must be Pass for all Step 6 controls before saving."
          )
        );
        return;
      }
      stepSaveMutation.mutate(buildStepPayload());
    } else {
      toast.info(t("This step is not yet implemented."));
    }
  };

  const handleBeginNextStep = () => {
    if (viewOnly) return;

    const stepNum = parseInt(detailStep, 10);
    if (stepNum === 2) {
      if (selectedReagents.length === 0) {
        toast.warning(
          t("Please select at least one reagent before proceeding.")
        );
        return;
      }
      if (!isStepSaved) {
        toast.warning(
          t("Please save step 2 changes before proceeding to the next step.")
        );
        return;
      }
    } else if (!isStepSaved) {
      toast.warning(
        t("Please save your changes before proceeding to the next step.")
      );
      return;
    }

    const nextStep = stepNum + 1;
    if (nextStep <= 6) {
      setPlateNote("");
      setDetailStep(String(nextStep));
      setEffectiveBatchStep((prev) => Math.max(prev, nextStep));
      setIsStepSaved(true);
    }
  };

  const markStepDirty = () => {
    if (!viewOnly) setIsStepSaved(false);
  };

  const handleReagentChange = (value: SelectOption[]) => {
    if (viewOnly) return;
    setSelectedReagents(value);
    markStepDirty();
  };

  const handleInstrumentChange = (value: string) => {
    if (viewOnly) return;
    setSelectedInstrument(value);
    markStepDirty();
  };

  const handleQpcrControlChange = (
    id: number,
    field: "ct" | "flag",
    value: string
  ) => {
    if (viewOnly) return;
    setQpcrControlRows((prev) => {
      const nextValue =
        field === "ct" ? String(value).replace(/[^0-9.]/g, "") : value;
      return prev.map((r) => {
        if (r.id !== id) return r;
        return { ...r, [field]: nextValue };
      });
    });
    markStepDirty();
  };

  const formatLogDate = (value: string | null) => toLocalDateTime(value);

  const handleOpenViewLog = async () => {
    if (!batchId) {
      toast.warning(t("Batch ID not found."));
      return;
    }

    setIsLogModalOpen(true);
    setIsLogLoading(true);
    try {
      const res = await GetBatchStepLogsAsync(batchId);
      const envelope = res?.data;
      const status = envelope?.statusCode ?? envelope?.StatusCode;
      if (status !== 200 && status !== 201) {
        toast.error(
          envelope?.message ??
            envelope?.Message ??
            t("Failed to load step logs.")
        );
        setStepLogs([]);
        return;
      }
      const data = envelope?.data ?? envelope?.Data ?? [];
      const mapped: StepLogEntry[] = Array.isArray(data)
        ? data.map((item: any) => {
            const stepNumber = Number(
              item?.stepNumber ?? item?.StepNumber ?? 0
            );
            const completedBy = item?.completedBy ?? item?.CompletedBy ?? null;
            const completedAt = item?.completedAt ?? item?.CompletedAt ?? null;
            const updatedBy = item?.updatedBy ?? item?.UpdatedBy ?? null;
            const updatedAt = item?.updatedAt ?? item?.UpdatedAt ?? null;

            return {
              stepNumber,
              completedBy,
              completedAt,
              updatedBy,
              updatedAt,
              log: buildStepLogText({
                stepNumber,
                completedBy,
                completedAt,
                updatedBy,
                updatedAt,
              }),
            };
          })
        : [];
      setStepLogs(mapped);
    } catch (error) {
      console.error(error);
      toast.error(t("An unexpected error occurred while loading logs."));
      setStepLogs([]);
    } finally {
      setIsLogLoading(false);
    }
  };

  const detailStepNum = parseInt(detailStep, 10);
  const isQuantificationStep = detailStepNum === 3 || detailStepNum === 5;
  const isStep5Quant = detailStepNum === 5;

  const getSaveBlockReason = (): string | null => {
    const stepNum = parseInt(detailStep);

    if (stepNum === 3 || stepNum === 5) {
      if (controlQuantificationRows.length > 0) {
        const incompleteControl = controlQuantificationRows.find(
          (r) => !hasRequiredQuantificationValues(r)
        );
        if (incompleteControl) {
          return t(
            `Control "${incompleteControl.controlName}" requires Nucleic Acid Conc., 260/280, and 260/230.`
          );
        }
        const failingControl = controlQuantificationRows.find((r) =>
          stepNum === 5
            ? r.gDNAFlag !== "Pass"
            : r.passFail !== "Pass" || r.gDNAFlag !== "Pass"
        );
        if (failingControl) {
          return stepNum === 5
            ? t(
                `Control "${failingControl.controlName}" has a failing gDNA Contamination Flag.`
              )
            : t(
                `Control "${failingControl.controlName}" has a failing Pass/Fail or gDNA Contamination Flag.`
              );
        }
      }
      if (fileDataRows.length === 0) {
        return t("At least one quantification row is required.");
      }
      const incompleteAccession = fileDataRows.find(
        (r) => !hasRequiredQuantificationValues(r)
      );
      if (incompleteAccession) {
        return t(
          `Accession "${incompleteAccession.accessionNumber || incompleteAccession.id}" requires Nucleic Acid Conc., 260/280, and 260/230.`
        );
      }
      const failingAccession = fileDataRows.find((r) => r.gDNAFlag !== "Pass");
      if (failingAccession) {
        return t(
          `Accession "${failingAccession.accessionNumber || failingAccession.id}" has a failing gDNA Contamination Flag.`
        );
      }
    }

    if (stepNum === 6) {
      if (qpcrControlRows.length === 0) return t("No controls loaded yet.");
      const missingCtControl = qpcrControlRows.find((r) => r.ct.trim() === "");
      if (missingCtControl) {
        return t(
          `Control "${missingCtControl.controlName}" requires a CT value.`
        );
      }
      const failingControl = qpcrControlRows.find((r) => r.flag !== "Pass");
      if (failingControl) {
        return t(
          `Control "${failingControl.controlName}" Pass/Fail must be set to Pass.`
        );
      }
    }

    return null;
  };

  return (
    <>
      {/* Main Row */}
      <TableRow className="h-30px">
        {/* Expand Toggle */}
        <TableCell className="text-center">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={row.rowStatus ? undefined : onToggleExpand}
            className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px"
          >
            {isOpen ? (
              <button
                id={`ViewRunsCloseExpand_${row.oncologyBatchId ?? index}`}
                className="btn btn-icon btn-icon-light btn-sm fw-bold rounded h-10px w-20px"
              >
                <RemoveICon />
              </button>
            ) : (
              <button
                id={`ViewRunsExpandOpen_${row.oncologyBatchId ?? index}`}
                className="btn btn-icon btn-icon-light btn-sm fw-bold btn-primary rounded h-20px w-20px min-h-20px"
              >
                <AddIcon />
              </button>
            )}
          </IconButton>
        </TableCell>

        {/* Checkbox */}
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={onToggleSelect}
            disabled={!!row.rowStatus}
          />
        </TableCell>

        {/* Actions */}
        <TableCell className="d-none">
          {!viewOnly &&
            (row.rowStatus ? (
              <div className="d-flex justify-content-center gap-2">
                <button
                  id={`OpenRunsSave_${row.oncologyBatchId ?? index}`}
                  disabled={saveMutation.isLoading}
                  className="btn btn-icon btn-sm fw-bold btn-table-save btn-icon-light h-32px w-32px fas-icon-20px"
                  onClick={handleSave}
                >
                  <i className="bi bi-check2" />
                </button>
                <button
                  id={`OpenRunsCancel_${row.oncologyBatchId ?? index}`}
                  className="btn btn-icon btn-sm fw-bold btn-table-cancel btn-icon-light h-32px w-32px fas-icon-20px"
                  onClick={handleRowCancel}
                >
                  <i className="bi bi-x" />
                </button>
              </div>
            ) : (
              <div className="d-flex justify-content-center">
                <StyledDropButtonThreeDots
                  id={`OpenRunsRow3Dots_${row.id ?? index}`}
                  aria-haspopup="true"
                  onClick={(e: any) => handleClick(e, "dropdown1")}
                  className="btn btn-light-info btn-sm btn-icon moreactions min-w-auto rounded-4"
                >
                  <i className="bi bi-three-dots-vertical p-0 icon" />
                </StyledDropButtonThreeDots>
                <StyledDropMenuMoreAction
                  anchorEl={anchorEl.dropdown1}
                  open={Boolean(anchorEl.dropdown1)}
                  onClose={() => handleClose("dropdown1")}
                  anchorOrigin={{ vertical: "top", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                  <PermissionComponent
                    moduleName="Oncology LIS"
                    pageName="View Runs"
                    permissionIdentifier="Completed"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <MenuItem className="p-0">
                        <a
                          className="w-150px p-0 text-dark"
                          onClick={() => {
                            handleClose("dropdown1");
                            toast.info(t("Completed — pending API."));
                          }}
                        >
                          <div className="menu-item px-3">
                            <i className="bi bi-check-circle-fill text-success mr-2 w-20px" />
                            {getPermissionDisplayName(
                              pageName,
                              moduleName,
                              permissionIdentifier,
                              "Completed"
                            )}
                          </div>
                        </a>
                      </MenuItem>
                    )}
                  </PermissionComponent>
                </StyledDropMenuMoreAction>
              </div>
            ))}
        </TableCell>

        {/* Batch Name */}
        <TableCell>
          {row.rowStatus && !viewOnly ? (
            <input
              type="text"
              name="batchName"
              placeholder={t("Batch Name")}
              className="form-control bg-white mb-lg-0 min-w-120px w-100 rounded h-30px"
              value={row.batchName ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{batchName}</div>
          )}
        </TableCell>

        {/* Batch # */}
        <TableCell>
          {row.rowStatus && !viewOnly ? (
            <input
              type="text"
              name="batchCode"
              placeholder={t("Batch #")}
              className="form-control bg-white mb-lg-0 min-w-100px w-100 rounded h-30px"
              value={row.batchCode ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{batchCode}</div>
          )}
        </TableCell>

        {/* Run Date */}
        <TableCell style={{ minWidth: "150px" }}>
          {row.rowStatus && !viewOnly ? (
            <SearchDatePicker
              name="runDate"
              value={row.runDate ?? ""}
              onChange={(e: any) => handleChange("runDate", e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{runDisplay}</div>
          )}
        </TableCell>

        {/* Plate Barcode No */}
        <TableCell>
          {row.rowStatus && !viewOnly ? (
            <input
              type="text"
              name="plateBarcodeNo"
              placeholder={t("Plate Barcode No")}
              className="form-control bg-white mb-lg-0 min-w-100px w-100 rounded h-30px"
              value={row.plateBarcodeNo ?? ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
            />
          ) : (
            <div style={{ width: "max-content" }}>{plateNo}</div>
          )}
        </TableCell>

        {/* Print Label */}
        <TableCell>
          {!row.rowStatus && (
            <button
              type="button"
              className="btn btn-sm btn-danger py-1 px-2"
              onClick={() => toast.info(t("Print label coming soon."))}
            >
              {t("Print Label")}
            </button>
          )}
        </TableCell>

        {/* Step # */}
        <TableCell>
          {row.rowStatus && !viewOnly ? (
            <select
              className="form-select bg-white w-100 rounded-2 fs-8 h-30px py-0"
              value={row.stepNumber ?? ""}
              onChange={(e) => handleChange("stepNumber", e.target.value)}
            >
              {STEP_OPTIONS.map((o) => (
                <option key={o.value || "all"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <div style={{ width: "max-content" }}>{displayedStepLabel}</div>
          )}
        </TableCell>
      </TableRow>

      {/* Expanded Detail Row */}
      {!row.rowStatus && (
        <TableRow>
          <TableCell
            colSpan={9}
            sx={{ py: 0, borderBottom: isOpen ? undefined : "none" }}
          >
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2, px: 2 }}>
                <Typography
                  variant="subtitle2"
                  className="fw-bold mb-2"
                  sx={{ color: "#333", fontSize: "14px" }}
                >
                  {t("Run Detail")}
                </Typography>

                <div
                  className="card"
                  style={{
                    borderRadius: "16px",
                    overflow: "hidden",
                    border: "1px solid #FFA800",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  {/* Header */}
                  <div
                    className="d-flex flex-wrap justify-content-between align-items-center px-4 py-3"
                    style={{
                      backgroundColor: "#FFF4DE",
                      borderBottom: "1px solid #FFA800",
                    }}
                  >
                    <h5 className="m-0 fw-bold" style={{ color: "#FFA800" }}>
                      {t("Plate Detail")}
                    </h5>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn-primary--icon px-7 text-white fw-500 rounded"
                        style={{
                          backgroundColor: "#FFA800",
                          border: "none",
                          padding: "0.65rem 1.75rem",
                          cursor: "pointer",
                        }}
                        onClick={handleOpenViewLog}
                      >
                        {t("View Log")}
                      </button>
                      <RunDetailStepSelect
                        t={t}
                        stepDropdownOptions={stepDropdownOptions}
                        detailStep={detailStep}
                        maxAllowedDetailStep={maxAllowedDetailStep}
                        isStepSaved={isStepSaved}
                        setPlateNote={setPlateNote}
                        setDetailStep={setDetailStep}
                        setIsStepSaved={setIsStepSaved}
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div
                    className="px-4 py-3"
                    style={{ backgroundColor: "#FFFFFF" }}
                  >
                    {stepDetailLoading ? (
                      <div className="d-flex flex-column align-items-center justify-content-center py-10">
                        <span
                          className="spinner-border text-warning mb-3"
                          role="status"
                        />
                        <Typography variant="body2" className="text-muted">
                          {t("Loading step data...")}
                        </Typography>
                      </div>
                    ) : (
                      <>
                        {/* Note Input */}
                        <label
                          className="form-label mb-1 fw-500"
                          style={{ fontSize: "13px", color: "#333" }}
                        >
                          {t("Note")}
                        </label>
                        <input
                          type="text"
                          className="form-control bg-white"
                          placeholder={t("Note...")}
                          value={plateNote}
                          readOnly={viewOnly}
                          onChange={
                            viewOnly
                              ? undefined
                              : (e) => {
                                  setPlateNote(e.target.value);
                                  markStepDirty();
                                }
                          }
                        />

                        {/* Step 2: Reagent Selection */}
                        {parseInt(detailStep) === 2 && (
                          <StepReagentSelect
                            reagentOptions={reagentOptions}
                            selectedReagents={selectedReagents}
                            onReagentChange={handleReagentChange}
                            isDisabled={viewOnly}
                          />
                        )}

                        {/* Step 3 / 5: quantification grid + optional file upload (accessions from API) */}
                        {isQuantificationStep && (
                          <div className="mt-3">
                            {controlQuantificationRows.length > 0 && (
                              <ControlQuantificationDataTable
                                t={t}
                                controlRows={controlQuantificationRows}
                                handleControlRowChange={
                                  handleControlQuantificationChange
                                }
                                isStep5Quant={isStep5Quant}
                                readOnly={viewOnly}
                              />
                            )}
                            <QuantificationDataTable
                              t={t}
                              fileDataRows={fileDataRows}
                              isStep5Quant={isStep5Quant}
                              handleFileDataChange={handleFileDataChange}
                              readOnly={viewOnly}
                            />

                            {!viewOnly && (
                              <>
                                <input
                                  type="file"
                                  ref={fileInputRef}
                                  style={{ display: "none" }}
                                  accept=".csv,.xlsx,.xls"
                                  onChange={handleFileUpload}
                                />
                                <button
                                  type="button"
                                  className="fw-500 rounded"
                                  style={{
                                    backgroundColor: isFileUploading
                                      ? "#D88A9E"
                                      : "#F1416C",
                                    border: "none",
                                    padding: "0.5rem 1.25rem",
                                    cursor: isFileUploading
                                      ? "not-allowed"
                                      : "pointer",
                                    color: "#fff",
                                    borderRadius: "6px",
                                    opacity: isFileUploading ? 0.7 : 1,
                                  }}
                                  disabled={isFileUploading}
                                  onClick={handleFileUploadClick}
                                >
                                  {isFileUploading
                                    ? t("Uploading...")
                                    : t("File Upload")}
                                </button>
                              </>
                            )}
                            <UploadedFileTable
                              t={t}
                              uploadedFile={uploadedFile}
                              onViewFile={handleViewUploadedFile}
                            />
                          </div>
                        )}

                        {/* Step 4: Reverse Transcription */}
                        {parseInt(detailStep) === 4 && (
                          <StepReagentInstrumentSelect
                            reagentOptions={reagentOptions}
                            instrumentOptions={instrumentOptions}
                            selectedReagents={selectedReagents}
                            selectedInstrument={selectedInstrument}
                            onReagentChange={handleReagentChange}
                            onInstrumentChange={handleInstrumentChange}
                            isDisabled={viewOnly}
                          />
                        )}

                        {/* Step 6: QPCR Control Plate */}
                        {parseInt(detailStep, 10) === 6 && (
                          <div
                            className="mt-3"
                            style={{
                              border: "1px solid #B388FF",
                              borderRadius: "10px",
                              overflow: "hidden",
                              backgroundColor: "#fff",
                            }}
                          >
                            <div
                              className="px-4 py-2 fw-bold"
                              style={{
                                backgroundColor: "#EDE7F6",
                                color: "#5E35B1",
                              }}
                            >
                              {t("Controls")}
                            </div>
                            <div
                              className="p-3"
                              style={{ backgroundColor: "#F7F7F7" }}
                            >
                              {!viewOnly && (
                                <div className="d-flex flex-wrap gap-2 mb-3">
                                  <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    accept=".csv,.xlsx,.xls"
                                    onChange={handleFileUpload}
                                  />
                                  <button
                                    type="button"
                                    className="fw-500 rounded"
                                    style={{
                                      backgroundColor: isFileUploading
                                        ? "#D88A9E"
                                        : "#F1416C",
                                      border: "none",
                                      padding: "0.4rem 1.2rem",
                                      cursor: isFileUploading
                                        ? "not-allowed"
                                        : "pointer",
                                      color: "#fff",
                                      opacity: isFileUploading ? 0.7 : 1,
                                    }}
                                    disabled={isFileUploading}
                                    onClick={handleFileUploadClick}
                                  >
                                    {isFileUploading
                                      ? t("Uploading...")
                                      : t("File Upload")}
                                  </button>
                                </div>
                              )}
                              <TableContainer className="shadow-none mb-3">
                                <Table
                                  stickyHeader
                                  aria-label="qpcr control table"
                                  className="plate-mapping-table mb-1"
                                >
                                  <TableHead className="h-40px">
                                    <TableRow>
                                      <TableCell className="min-w-55px w-55px">
                                        {t("Control Name")}
                                      </TableCell>
                                      <TableCell
                                        style={{
                                          minWidth: "140px",
                                          width: "140px",
                                        }}
                                      >
                                        {t("CT")} *
                                      </TableCell>
                                      <TableCell
                                        style={{
                                          minWidth: "155px",
                                          width: "155px",
                                        }}
                                      >
                                        {t("Pass/Fail")}
                                      </TableCell>
                                      <TableCell className="min-w-250px">
                                        {t("Warning")}
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody className="scroll-start">
                                    {qpcrControlRows.map((control) => (
                                      <TableRow key={control.id}>
                                        <TableCell>
                                          {control.controlName}
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            minWidth: "140px",
                                            width: "140px",
                                          }}
                                        >
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            autoComplete="off"
                                            className="form-control bg-transparent"
                                            value={control.ct}
                                            required
                                            readOnly={viewOnly}
                                            onChange={
                                              viewOnly
                                                ? undefined
                                                : (e) =>
                                                    handleQpcrControlChange(
                                                      control.id,
                                                      "ct",
                                                      e.target.value
                                                    )
                                            }
                                          />
                                        </TableCell>
                                        <TableCell
                                          style={{
                                            minWidth: "155px",
                                            width: "155px",
                                          }}
                                        >
                                          <select
                                            className="form-select bg-white"
                                            value={control.flag}
                                            disabled={viewOnly}
                                            onChange={
                                              viewOnly
                                                ? undefined
                                                : (e) =>
                                                    handleQpcrControlChange(
                                                      control.id,
                                                      "flag",
                                                      e.target.value
                                                    )
                                            }
                                          >
                                            <option value="">
                                              {t("Select")}
                                            </option>
                                            {PASS_FAIL_OPTIONS.map((option) => (
                                              <option
                                                key={option.value}
                                                value={option.value}
                                              >
                                                {t(option.label)}
                                              </option>
                                            ))}
                                          </select>
                                        </TableCell>
                                        <TableCell>
                                          <span className="text-danger fw-500">
                                            {control.warning || ""}
                                          </span>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              <UploadedFileTable
                                t={t}
                                uploadedFile={uploadedFile}
                                onViewFile={handleViewUploadedFile}
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                {!stepDetailLoading && !viewOnly && (
                  <div className="d-flex gap-2 mt-3">
                    <button
                      type="button"
                      className="btn-primary--icon px-7 text-white fw-500 rounded"
                      style={{
                        backgroundColor: canSaveCurrentStep()
                          ? "#50CD89"
                          : "#A0D8A0",
                        border: "none",
                        padding: "0.65rem 1.75rem",
                        cursor:
                          stepSaveMutation.isLoading || !canSaveCurrentStep()
                            ? "not-allowed"
                            : "pointer",
                        opacity:
                          stepSaveMutation.isLoading || !canSaveCurrentStep()
                            ? 0.7
                            : 1,
                      }}
                      disabled={
                        stepSaveMutation.isLoading || !canSaveCurrentStep()
                      }
                      onClick={handleSaveStepChanges}
                    >
                      {stepSaveMutation.isLoading
                        ? t("Saving...")
                        : t("Save Changes")}
                    </button>
                    {NEXT_STEP_ACTION[detailStep] && (
                      <button
                        type="button"
                        className="btn-primary--icon px-7 text-white fw-500 rounded"
                        style={{
                          backgroundColor: !canBeginNextStep()
                            ? "#A0C4E8"
                            : "#5E9BD7",
                          border: "none",
                          padding: "0.65rem 1.75rem",
                          cursor: !canBeginNextStep()
                            ? "not-allowed"
                            : "pointer",
                          opacity: !canBeginNextStep() ? 0.7 : 1,
                        }}
                        disabled={
                          !canBeginNextStep() || stepSaveMutation.isLoading
                        }
                        onClick={handleBeginNextStep}
                      >
                        {t(NEXT_STEP_ACTION[detailStep])}
                      </button>
                    )}
                    {/* ↓ paste here — sits below the buttons, inside the same conditional block */}
                    {getSaveBlockReason() && (
                      <div
                        className="d-flex align-items-center gap-2"
                        style={{
                          color: "#F1416C",
                          fontSize: "12.5px",
                          fontWeight: 500,
                          padding: "6px 10px",
                          backgroundColor: "#FFF5F8",
                          border: "1px solid #F1416C",
                          borderRadius: "6px",
                          maxWidth: "fit-content",
                        }}
                      >
                        <i
                          className="bi bi-exclamation-circle-fill"
                          style={{ fontSize: "14px" }}
                        />
                        {getSaveBlockReason()}
                      </div>
                    )}
                  </div>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}

      <Modal
        open={isLogModalOpen}
        onClose={() => setIsLogModalOpen(false)}
        aria-labelledby="view-runs-step-log-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", md: "780px" },
            maxHeight: "85vh",
            overflowY: "auto",
            borderRadius: "14px",
            border: "1px solid #6C757D",
            backgroundColor: "#F8F9FA",
            boxShadow: "0px 12px 30px rgba(0,0,0,0.2)",
          }}
        >
          <div
            className="d-flex justify-content-between align-items-center px-4 py-3"
            style={{
              backgroundColor: "#FFF4DE",
              borderBottom: "1px solid #FFA800",
            }}
          >
            <h5
              id="view-runs-step-log-title"
              className="m-0 fw-bold"
              style={{ color: "#FFA800" }}
            >
              {t("Batch Step Logs")}
            </h5>
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => setIsLogModalOpen(false)}
            >
              {t("Close")}
            </button>
          </div>

          <div className="px-4 py-3" style={{ backgroundColor: "#F8F9FA" }}>
            {isLogLoading ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-8">
                <span
                  className="spinner-border text-warning mb-2"
                  role="status"
                />
                <Typography variant="body2" className="text-muted">
                  {t("Loading logs...")}
                </Typography>
              </div>
            ) : stepLogs.length === 0 ? (
              <div
                className="text-center py-8 text-muted rounded"
                style={{
                  border: "1px dashed #ADB5BD",
                  backgroundColor: "#FFFFFF",
                }}
              >
                {t("No logs found for this batch.")}
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {stepLogs
                  .slice()
                  .sort((a, b) => (a.stepNumber ?? 0) - (b.stepNumber ?? 0))
                  .map((item, idx) => (
                    <div
                      key={`${item.stepNumber}-${idx}`}
                      className="p-3"
                      style={{
                        borderRadius: "10px",
                        border: "1px solid #CED4DA",
                        backgroundColor: idx % 2 === 0 ? "#FFFFFF" : "#F8F9FA",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.06)",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span
                          className="fw-bold px-3 py-1 rounded-pill"
                          style={{
                            backgroundColor: "#E9ECEF",
                            color: "#343A40",
                            fontSize: "12px",
                          }}
                        >
                          {t("Step")} {item.stepNumber}
                        </span>
                        <span style={{ fontSize: "12px", color: "#7E8299" }}>
                          {formatLogDate(item.completedAt)}
                        </span>
                      </div>

                      <div
                        className="mb-2"
                        style={{ color: "#3F4254", fontSize: "13px" }}
                      >
                        {item.log}
                      </div>

                      <div
                        className="d-flex flex-wrap gap-3"
                        style={{ fontSize: "12px", color: "#7E8299" }}
                      >
                        <span>
                          {t("Completed By")}:{" "}
                          <strong>{item.completedBy || "—"}</strong>
                        </span>
                        {item.updatedBy ? (
                          <span>
                            {t("Updated By")}: <strong>{item.updatedBy}</strong>
                          </span>
                        ) : null}
                        {item.updatedAt ? (
                          <span>
                            {t("Updated At")}:{" "}
                            <strong>{formatLogDate(item.updatedAt)}</strong>
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default OpenRunsRow;
